import React from 'react';
import { useIntl } from '@leagueplatform/locales';
import { captureError } from '@leagueplatform/observability';
import {
  EnvironmentKey,
  isImportExportEnabled,
  Nullable,
  filterEntitiesToImport,
  ConfigEntityDetail,
  DomainEntity,
  type BulkActionOperationType,
  type EntityStatusFilter,
} from '@web-config-app/core';
import { isUsingConfigAppBackend } from '@web-config-app/common';
import { useStore } from 'zustand';
import type { AvailableEnvironment } from '../../types/multi-environment.types';
import type { BulkActionSession } from '../../bulk-action-reporting/bulk-action-reporting.types';
import { createBulkActionReportingStore } from '../../bulk-action-reporting/bulk-action-reporting.store';
import { useImportEntitiesIndexedDB } from '../../hooks/use-import-entities-indexed-db/use-import-entities-indexed-db';
import { useImportEntities } from '../../hooks/use-import-entities/use-import-entities';
import { useExportEntities } from '../../hooks/use-export-entities/use-export-entities';
import {
  useCopyEntitiesToEnvironment,
  type CopyTargetOverride,
} from '../../hooks/use-copy-entities-to-environment/use-copy-entities-to-environment';
import {
  BULK_PROMOTE_ALL_ACTION,
  BULK_PROMOTE_SELECTED_ACTION,
  bulkActionIdForTarget,
} from './bulk-action-target-id';
import { useCopyAllEntitiesToEnvironmentBulkAction } from '../../hooks/use-copy-all-entities-to-environment-bulk-action/use-copy-all-entities-to-environment-bulk-action';
import { useConfigEntityGetByIdQuery } from '../../hooks/use-config-entity-get-by-id-query/use-config-entity-get-by-id-query';
import { useBulkArchiveEntities } from '../../hooks/use-bulk-archive-entities/use-bulk-archive-entities';
import { useBulkDeleteEntities } from '../../hooks/use-bulk-delete-entities/use-bulk-delete-entities';

/**
 * Configuration for a confirmation modal that appears before executing an action
 */
export interface BulkActionConfirmationModal {
  title: string;
  description: string;
  confirmCta: string;
  destructive?: boolean;
}

/**
 * Represents a menu item for bulk actions
 */
export interface BulkActionMenuItem {
  id: string;
  label: React.ReactNode;
  action?: (...args: any[]) => void;
  selected?: boolean;
  disabledTooltip?: string;
  confirmationModal?: BulkActionConfirmationModal;
  keepOpenOnSelect?: boolean;
  /**
   * The environment this action promotes to, for actions that exist once per target. Lets a
   * consumer name the target -- in a confirmation modal, say -- without parsing `label` or the id.
   */
  target?: CopyTargetOverride;
}

/**
 * Represents a group of bulk action menu items
 */
export interface BulkActionMenuItemGroup {
  menuItems: BulkActionMenuItem[];
}

export interface BulkActionsContextType {
  actions: Array<BulkActionMenuItem>;
  reportingSession?: BulkActionSession;
  clearReportingSession?: () => void;
  cancelActiveOperation?: () => void;
  retryActiveOperation?: () => void;
  activeReportingOperation: BulkActionOperationType | null;
  setActiveReportingOperation: (
    operation: BulkActionOperationType | null,
  ) => void;
  operationStartedAt: number | null;
  setOperationStartedAt: (timestamp: number) => void;
  resetBulkActionReportingState: () => void;
  /** Whether the bulk deep publish confirmation modal is open */
  isBulkPublishOpen: boolean;
  /** Open or close the bulk deep publish confirmation modal */
  setIsBulkPublishOpen: (open: boolean) => void;
  /** Entities selected for bulk publish */
  bulkPublishSelectedEntities: ConfigEntityDetail[];
  /** The underlying reporting store for consumers that need direct access */
  bulkActionReportingStore: ReturnType<typeof createBulkActionReportingStore>;
}

export const BulkActionsContext =
  React.createContext<BulkActionsContextType | null>(null);

export interface BulkActionsProviderProps extends React.PropsWithChildren {
  currentAuthoringEnvironment: EnvironmentKey;
  entityName: string;
  availableEnvironments: Nullable<AvailableEnvironment[]>;
  otherAuthoringEnvironment: EnvironmentKey | undefined;
  domainEntityType: string;
  tenantId: string;
  domainEntity: DomainEntity;
}

export const BulkActionsProvider: React.FC<BulkActionsProviderProps> = ({
  children,
  currentAuthoringEnvironment,
  otherAuthoringEnvironment: otherAuthoringEnvironmentKey,
  entityName,
  availableEnvironments,
  domainEntityType,
  tenantId,
  domainEntity,
}) => {
  const { formatMessage } = useIntl();

  const [activeDetailedOperation, setActiveDetailedOperation] =
    React.useState<BulkActionOperationType | null>(null);
  const [operationStartedAt, setOperationStartedAt] = React.useState<
    number | null
  >(null);
  const [isBulkPublishOpen, setIsBulkPublishOpen] = React.useState(false);
  const [bulkPublishSelectedEntities, setBulkPublishSelectedEntities] =
    React.useState<ConfigEntityDetail[]>([]);
  const bulkActionReportingStore = React.useMemo(
    () => createBulkActionReportingStore(),
    [],
  );

  const bulkActionReportingSession = useStore(
    bulkActionReportingStore,
    (state) => state.session,
  );

  const { entities, clearEntitiesOfType } = useImportEntitiesIndexedDB({
    domainEntityType,
  });
  const entitiesToImport = React.useMemo(
    () => filterEntitiesToImport(entities),
    [entities],
  );

  const entityAmount = entitiesToImport.length;

  // Every other environment is a target, each with its own action below. Collapsing to a
  // single unambiguous one used to hide bulk promote entirely from multi-preview tenants --
  // exactly the tenants that need it, for seeding a new test environment from an existing one.
  const bulkPromoteTargets = React.useMemo(
    () =>
      availableEnvironments?.filter(
        (env) => env.id !== currentAuthoringEnvironment && Boolean(env.label),
      ) ?? [],
    [availableEnvironments, currentAuthoringEnvironment],
  );

  const isImportExportEnabledFlag = isImportExportEnabled();
  const isConfigAppBackend = isUsingConfigAppBackend({
    entityLevelOverride: domainEntity.isUsingConfigAppBackend,
  });
  const { handleExportAllEntities, retryFailedExports, cancelExport } =
    useExportEntities({
      domainEntityType,
      currentAuthoringEnvironment,
      domainEntityName: entityName,
      tenantId,
      bulkActionReportingStore,
    });

  const { importAllEntities, retryFailedImports, cancelImport } =
    useImportEntities({
      domainEntityType,
      currentAuthoringEnvironment,
      tenantId,
      bulkActionReportingStore,
    });

  // Fetch function to refetch entities from source environment
  const fetchEntityOnSourceById = useConfigEntityGetByIdQuery({
    sourceEnvironment: currentAuthoringEnvironment,
    domainEntityType,
  });

  // Preprocess function to refetch entities before promotion
  // This ensures we have fresh data since selection state may be stale
  const preprocessEntity = React.useCallback(
    async (entity: ConfigEntityDetail) => {
      const { data } = await fetchEntityOnSourceById(entity.id);
      return data;
    },
    [fetchEntityOnSourceById],
  );

  // Both copy hooks are handed an explicit target per call, so this only stands in for a
  // caller that never uses those actions. The fallback keeps the hook calls unconditional.
  const configuredTargetEnvironmentName = React.useMemo(
    () =>
      availableEnvironments?.find(
        (env) => env.id === otherAuthoringEnvironmentKey,
      )?.label ?? '',
    [availableEnvironments, otherAuthoringEnvironmentKey],
  );

  const { copyEntitiesToEnvironment, cancelCopy: cancelCopySelected } =
    useCopyEntitiesToEnvironment({
      sourceEnvironment: currentAuthoringEnvironment,
      targetEnvironment:
        otherAuthoringEnvironmentKey ?? currentAuthoringEnvironment,
      targetEnvironmentName: configuredTargetEnvironmentName,
      schema: domainEntity.schema,
      preprocessEntity,
      domainEntityType,
      tenantId,
      bulkActionReportingStore,
    });

  const { copyAllEntitiesToEnvironment, cancelCopyAll, retryFailedCopyAll } =
    useCopyAllEntitiesToEnvironmentBulkAction({
      domainEntityType,
      domainEntityName: entityName,
      sourceEnvironment: currentAuthoringEnvironment,
      targetEnvironment:
        otherAuthoringEnvironmentKey ?? currentAuthoringEnvironment,
      targetEnvironmentName: configuredTargetEnvironmentName,
      schema: domainEntity.schema,
      tenantId,
      bulkActionReportingStore,
    });

  const handleCopyEntitiesToEnvironment = React.useCallback(
    async (
      selectedEntities: ConfigEntityDetail[],
      target?: CopyTargetOverride,
    ) => {
      try {
        await copyEntitiesToEnvironment(selectedEntities, target);
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') {
          return;
        }
        throw error;
      }
    },
    [copyEntitiesToEnvironment],
  );

  const { handleBulkArchiveEntities, cancelArchive } = useBulkArchiveEntities({
    currentAuthoringEnvironment,
    domainEntityType,
    bulkActionReportingStore,
  });

  const { handleBulkDeleteEntities, cancelDelete } = useBulkDeleteEntities({
    currentAuthoringEnvironment,
    domainEntityType,
    bulkActionReportingStore,
  });

  const handleClearAndExport = React.useCallback(async () => {
    await clearEntitiesOfType();
    await handleExportAllEntities();
  }, [clearEntitiesOfType, handleExportAllEntities]);

  const menuGroups: BulkActionMenuItemGroup[] = React.useMemo(() => {
    const items: BulkActionMenuItem[] = [];

    if (isImportExportEnabledFlag) {
      items.push({
        id: 'export-all-entities',
        label: formatMessage({ id: 'EXPORT_ALL_ENTITY_NAME' }, { entityName }),
        action:
          entityAmount > 0 ? handleClearAndExport : handleExportAllEntities,
        ...(entityAmount > 0 && {
          confirmationModal: {
            title: formatMessage(
              {
                id: 'DISCARD_EXISTING_EXPORTED_DATA_TITLE',
              },
              { entityName },
            ),
            description: formatMessage(
              {
                id: 'DISCARD_EXISTING_EXPORTED_DATA_DESCRIPTION',
              },
              { entityName },
            ),
            confirmCta: formatMessage(
              {
                id: 'DISCARD_EXISTING_AND_EXPORT_NEW',
              },
              { entityName },
            ),
            destructive: true,
          },
        }),
      });
      items.push({
        id: 'import-all-entities',
        label:
          entityAmount > 0
            ? formatMessage(
                { id: 'IMPORT_ENTITY_NAME' },
                { entityName, count: entityAmount },
              )
            : formatMessage({ id: 'NO_ENTITIES_TO_IMPORT' }, { entityName }),
        action: importAllEntities,
        disabledTooltip:
          entityAmount === 0
            ? formatMessage(
                { id: 'NO_ENTITIES_TO_IMPORT_TOOLTIP' },
                { entityName },
              )
            : undefined,
      });
      items.push({
        id: 'clear-all-entities-import-memory',
        label: formatMessage(
          { id: 'CLEAR_ENTITY_NAME_FROM_IMPORT_MEMORY' },
          { entityName },
        ),
        action: clearEntitiesOfType,
        keepOpenOnSelect: true,
        disabledTooltip:
          entityAmount === 0
            ? formatMessage(
                { id: 'NO_ENTITIES_TO_CLEAR_TOOLTIP' },
                { entityName },
              )
            : undefined,
      });
    }

    // One pair per target, each closing over the environment it names. The id carries the
    // target so a consumer can find the whole family by prefix -- see bulkActionIdForTarget.
    bulkPromoteTargets.forEach((target) => {
      const targetOverride: CopyTargetOverride = {
        environmentKey: target.id,
        name: target.label,
      };

      items.push({
        id: bulkActionIdForTarget(BULK_PROMOTE_ALL_ACTION, targetOverride),
        target: targetOverride,
        label: formatMessage(
          { id: 'COPY_ALL_ENTITY_NAME_TO_ENVIRONMENT_AS_DRAFT' },
          { entityName, environmentName: target.label },
        ),
        action: (options?: { status?: EntityStatusFilter }) =>
          copyAllEntitiesToEnvironment({ ...options, target: targetOverride }),
      });

      items.push({
        id: bulkActionIdForTarget(BULK_PROMOTE_SELECTED_ACTION, targetOverride),
        target: targetOverride,
        label: formatMessage(
          { id: 'PROMOTE_SELECTED_ENTITIES' },
          { entityName, environmentName: target.label },
        ),
        action: (selectedEntities: ConfigEntityDetail[]) => {
          setActiveDetailedOperation('copyToEnvironment');
          setOperationStartedAt(Date.now());
          handleCopyEntitiesToEnvironment(selectedEntities, targetOverride);
        },
      });
    });

    if (isConfigAppBackend) {
      items.push({
        id: 'bulk-publish-selected-entities',
        label: formatMessage(
          { id: 'BULK_PUBLISH_SELECTED_ENTITIES' },
          { entityName },
        ),
        action: (selectedEntities: ConfigEntityDetail[]) => {
          setBulkPublishSelectedEntities(selectedEntities);
          setIsBulkPublishOpen(true);
        },
      });

      items.push({
        id: 'bulk-archive-selected-entities',
        label: formatMessage(
          { id: 'BULK_ARCHIVE_SELECTED_ENTITIES' },
          { entityName },
        ),
        action: (...args: Parameters<typeof handleBulkArchiveEntities>) => {
          setActiveDetailedOperation('archive');
          setOperationStartedAt(Date.now());
          return handleBulkArchiveEntities(...args);
        },
        confirmationModal: {
          title: formatMessage(
            { id: 'BULK_ARCHIVE_CONFIRM_TITLE' },
            { entityName },
          ),
          description: formatMessage(
            { id: 'BULK_ARCHIVE_CONFIRM_DESCRIPTION' },
            { entityName },
          ),
          confirmCta: formatMessage(
            { id: 'BULK_ARCHIVE_CONFIRM_CTA' },
            { entityName },
          ),
          destructive: true,
        },
      });
      items.push({
        id: 'bulk-delete-selected-entities',
        label: formatMessage(
          { id: 'BULK_DELETE_SELECTED_ENTITIES' },
          { entityName },
        ),
        action: (...args: Parameters<typeof handleBulkDeleteEntities>) => {
          setActiveDetailedOperation('delete');
          setOperationStartedAt(Date.now());
          return handleBulkDeleteEntities(...args);
        },
        confirmationModal: {
          title: formatMessage(
            { id: 'BULK_DELETE_CONFIRM_TITLE' },
            { entityName },
          ),
          description: formatMessage(
            { id: 'BULK_DELETE_CONFIRM_DESCRIPTION' },
            { entityName },
          ),
          confirmCta: formatMessage(
            { id: 'BULK_DELETE_CONFIRM_CTA' },
            { entityName },
          ),
          destructive: true,
        },
      });
    }

    return [{ menuItems: items }];
  }, [
    isImportExportEnabledFlag,
    isConfigAppBackend,
    bulkPromoteTargets,
    formatMessage,
    entityName,
    handleExportAllEntities,
    handleClearAndExport,
    entityAmount,
    importAllEntities,
    clearEntitiesOfType,
    handleCopyEntitiesToEnvironment,
    copyAllEntitiesToEnvironment,
    handleBulkArchiveEntities,
    handleBulkDeleteEntities,
  ]);

  const clearReportingSession = React.useCallback(() => {
    bulkActionReportingStore.getState().clearSession();
  }, [bulkActionReportingStore]);

  const cancelActiveOperation = React.useCallback(() => {
    cancelExport();
    cancelImport();
    cancelCopySelected();
    cancelCopyAll();
    cancelArchive();
    cancelDelete();
  }, [
    cancelExport,
    cancelImport,
    cancelCopySelected,
    cancelCopyAll,
    cancelArchive,
    cancelDelete,
  ]);

  const resetBulkActionReportingState = React.useCallback(() => {
    setActiveDetailedOperation(null);
    bulkActionReportingStore.getState().clearSession();
  }, [bulkActionReportingStore]);

  const retryActiveOperation = React.useMemo(() => {
    if (activeDetailedOperation === 'export') {
      return () => {
        // retryFailedExports handles expected errors internally (network,
        // abort, partial failure). This catch is a safety net for unexpected
        // errors (e.g. a bug in the store) to avoid unhandled rejections.
        retryFailedExports().catch((error) => {
          captureError(
            error instanceof Error ? error : new Error(String(error)),
            { errorName: 'RetryExportFailure' },
          );
        });
      };
    }
    if (activeDetailedOperation === 'import') {
      return () => {
        // retryFailedImports handles expected errors internally (network,
        // abort, partial failure). This catch is a safety net for unexpected
        // errors (e.g. a bug in the store) to avoid unhandled rejections.
        retryFailedImports().catch((error) => {
          captureError(
            error instanceof Error ? error : new Error(String(error)),
            { errorName: 'RetryImportFailure' },
          );
        });
      };
    }
    if (activeDetailedOperation === 'copyToEnvironment') {
      return () => {
        // Use retryFailedCopyAll which handles both copy-all and copy-selected
        // retries — it recovers the status filter from the session, falls back
        // to a fresh copy-all when no session exists, and delegates to
        // retryFailedCopy for the actual copy phase.
        retryFailedCopyAll().catch((error) => {
          captureError(
            error instanceof Error ? error : new Error(String(error)),
            { errorName: 'RetryCopyToEnvironmentFailure' },
          );
        });
      };
    }
    // Only operations with retry support return a handler;
    // undefined signals that retry is not available.
    return undefined;
  }, [
    activeDetailedOperation,
    retryFailedExports,
    retryFailedImports,
    retryFailedCopyAll,
  ]);

  const value = React.useMemo<BulkActionsContextType>(
    () => ({
      actions: menuGroups[0]?.menuItems ?? [],
      reportingSession: bulkActionReportingSession,
      clearReportingSession,
      cancelActiveOperation,
      retryActiveOperation,
      activeReportingOperation: activeDetailedOperation,
      setActiveReportingOperation: setActiveDetailedOperation,
      operationStartedAt,
      setOperationStartedAt,
      resetBulkActionReportingState,
      isBulkPublishOpen,
      setIsBulkPublishOpen,
      bulkPublishSelectedEntities,
      bulkActionReportingStore,
    }),
    [
      menuGroups,
      bulkActionReportingSession,
      clearReportingSession,
      cancelActiveOperation,
      retryActiveOperation,
      activeDetailedOperation,
      operationStartedAt,
      resetBulkActionReportingState,
      isBulkPublishOpen,
      bulkPublishSelectedEntities,
      bulkActionReportingStore,
    ],
  );

  return (
    <BulkActionsContext.Provider value={value}>
      {children}
    </BulkActionsContext.Provider>
  );
};

BulkActionsProvider.displayName = 'BulkActionsProvider';
